import React, { useMemo, useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { Purchase } from '../../types';
import Input from './Input';
import Select from './Select';
import AsyncSelect from './AsyncSelect';
import TextArea from './TextArea';
import Button from './Button';
import { providersService, categoriesService, billsService } from '../../services';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

interface PurchaseFormProps {
  purchase?: Purchase;
  onSubmit: (data: Partial<Purchase>) => void;
  onCancel: () => void;
}

const PurchaseForm: React.FC<PurchaseFormProps> = ({ purchase, onSubmit, onCancel }) => {
  // Fetch currencies from API
  const { data: currencies } = useCurrencies();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    control
  } = useForm<Purchase>({
    defaultValues: purchase ? {
      ...purchase,
      // Normalize populated currency relation object to its documentId
      currency: typeof (purchase as any).currency === 'object' && (purchase as any).currency
        ? ((purchase as any).currency.documentId || (purchase as any).currency.id || '')
        : (purchase.currency || ''),
      orderDate: formatDateForInput(purchase.orderDate),
      deliveryDate: formatDateForInput(purchase.deliveryDate)
    } : {
      providerId: '',
      categoryId: '',
      billId: '',
      items: [],
      total: 0,
      currency: '',
      exchangeRate: undefined,
      status: 'pending',
      orderDate: '',
      deliveryDate: '',
      notes: ''
    }
  });

  // Watch the currency field to conditionally show exchange rate
  const selectedCurrency = useWatch({
    control,
    name: 'currency',
    defaultValue: ''
  });

  // Currencies load async: once options exist, re-apply the saved currency
  // (a native select silently drops a value that has no matching option yet)
  useEffect(() => {
    if (!purchase || !currencies?.length) return;
    const raw: any = (purchase as any).currency;
    const savedCurrencyId = raw && typeof raw === 'object' ? (raw.documentId || raw.id) : raw;
    if (savedCurrencyId) {
      const match = currencies.find(
        (c: any) => c.documentId === savedCurrencyId || c.id?.toString() === savedCurrencyId.toString() || c.code === savedCurrencyId
      );
      if (match) {
        setValue('currency', (match as any).documentId || (match as any).id?.toString() || '');
      }
    }
  }, [purchase, currencies, setValue]);

  // Server-side search + pagination for provider and category dropdowns
  const fetchProviders = async (query: string, page: number) => {
    return providersService.getAll({
      filters: query ? { name: { $containsi: query } } : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['name:asc']
    }) as any;
  };

  const fetchCategories = async (query: string, page: number) => {
    return categoriesService.getAll({
      filters: query ? { name: { $containsi: query } } : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['name:asc']
    }) as any;
  };

  const fetchBills = async (query: string, page: number) => {
    return billsService.getAll({
      filters: query ? { description: { $containsi: query } } : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['createdAt:desc']
    }) as any;
  };

  const billOptionLabel = (bill: any) => {
    const code = resolveCurrencyCode(bill.currency);
    const amount = Number(bill.amount || 0).toLocaleString();
    return `Bill #${bill.id} — ${bill.description || 'No description'} (${amount} ${code})`;
  };

  // Currency options from API - NO HARDCODED VALUES
  const currencyOptions = useMemo(() => {
    const options = (currencies || [])
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }));
    return [{ value: '', label: 'Select currency' }, ...options];
  }, [currencies]);

  // Resolve selected currency (documentId) to its code for USD checks
  const selectedCurrencyCode = useMemo(() => {
    const found = (currencies || []).find(
      (c: any) => c.documentId === selectedCurrency || c.id?.toString() === selectedCurrency?.toString()
    );
    return found?.code || (typeof selectedCurrency === 'string' ? selectedCurrency : '');
  }, [currencies, selectedCurrency]);

  const handleFormSubmit = (data: Purchase) => {
    // If currency is not USD, remove exchange rate
    const submitData = {
      ...data,
      orderDate: formatDateForAPI(data.orderDate),
      deliveryDate: formatDateForAPI(data.deliveryDate),
      exchangeRate: selectedCurrencyCode === 'USD' ? data.exchangeRate : undefined,
      // For simplicity, we'll create a single item from the form data
      // (keep the existing record's documentId so it's updated, not recreated)
      items: [{
        id: purchase?.items?.[0]?.id || '1',
        documentId: purchase?.items?.[0]?.documentId,
        name: data.notes || 'Purchase Item',
        quantity: 1,
        unitPrice: data.total,
        total: data.total
      }]
    };

    onSubmit({
      ...submitData,
      ...(purchase ? { id: purchase.id, documentId: purchase.documentId } : {})
    });
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'delivered', label: 'Delivered' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {purchase ? 'Edit Purchase Order' : 'Add New Purchase Order'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="providerId"
            control={control}
            rules={{ required: 'Provider is required' }}
            render={({ field }) => (
              <AsyncSelect
                label="Provider"
                name="providerId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.providerId?.message as string}
                required
                placeholder="Select provider..."
                fetchOptions={fetchProviders}
                getOptionLabel={(provider) => provider.name}
                getOptionValue={(provider) => provider.documentId || provider.id}
              />
            )}
          />

          <Select
            label="Status"
            {...register('status', { required: 'Status is required' })}
            options={statusOptions}
            error={errors.status?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="categoryId"
            control={control}
            render={({ field }) => (
              <AsyncSelect
                label="Category"
                name="categoryId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.categoryId?.message as string}
                placeholder="Select category..."
                fetchOptions={fetchCategories}
                getOptionLabel={(cat) => cat.name}
                getOptionValue={(cat) => cat.documentId || cat.id?.toString()}
              />
            )}
          />
          <div>
            <Controller
              name="billId"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  label="Bill (optional)"
                  name="billId"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.billId?.message as string}
                  placeholder="Link to a bill..."
                  fetchOptions={fetchBills}
                  getOptionLabel={billOptionLabel}
                  getOptionValue={(bill) => bill.documentId || bill.id?.toString()}
                />
              )}
            />
            <p className="mt-1 text-xs text-gray-500">
              When a bill is linked, the paid/remaining amounts are detected from it in the purchases report
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Order Date"
            type="date"
            {...register('orderDate', { required: 'Order date is required' })}
            error={errors.orderDate?.message}
          />

          <Input
            label="Delivery Date"
            type="date"
            {...register('deliveryDate')}
            error={errors.deliveryDate?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            error={errors.currency?.message}
          />

          <Input
            label="Total Amount"
            type="number"
            step="0.01"
            {...register('total', { 
              required: 'Total amount is required',
              min: { value: 0.01, message: 'Total must be greater than 0' }
            })}
            error={errors.total?.message}
            placeholder="0.00"
          />
        </div>

        {/* Conditionally show exchange rate field only for USD */}
        {selectedCurrencyCode === 'USD' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Exchange Rate (USD to EGP)"
              type="number"
              step="0.01"
              {...register('exchangeRate', { 
                required: selectedCurrencyCode === 'USD' ? 'Exchange rate is required for USD' : false,
                min: { value: 0.01, message: 'Exchange rate must be greater than 0' }
              })}
              error={errors.exchangeRate?.message}
              placeholder="e.g., 30.50"
            />
            <div></div> {/* Empty div for grid alignment */}
          </div>
        )}

        <TextArea
          label="Notes"
          {...register('notes')}
          error={errors.notes?.message}
          placeholder="Enter purchase details, item descriptions, or additional notes"
          rows={4}
        />

        {/* Display calculated EGP amount if USD is selected */}
        {selectedCurrencyCode === 'USD' && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> If you enter amounts in USD with an exchange rate, the EGP equivalent will be calculated automatically.
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {purchase ? 'Update Purchase Order' : 'Create Purchase Order'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseForm;